import React from "react";
import { MuiThemeProvider, createTheme } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider, Calendar } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { Paper } from "@material-ui/core";
import { format, isSameDay, isToday } from "date-fns";
import { PRIMARY_THEME_COLOR } from "../../utils/styles/theme.colors";

const theme = createTheme({
    palette: {
        primary: {
            light: PRIMARY_THEME_COLOR,
            main: PRIMARY_THEME_COLOR,
            dark: PRIMARY_THEME_COLOR,
        },
    },
    overrides: {
        MuiPickersCalendarHeader: {
            switchHeader: {
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
                marginTop: -5,
                padding: "0 8px",
                borderBottom: "1px solid var(--primary-popup-stroke-color)",
                paddingBottom: "4px",
            },
            transitionContainer: {
                position: "relative",
                top: "auto",
                left: "auto",
                transform: "none",
                order: -1,
                textAlign: "left",
                width: "auto",
                flex: "1",
                "& > p": {
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "var(--primary-text-color)",
                },
            },
            iconButton: {
                padding: "4px",
                order: 1,
                borderRadius: 0,
                backgroundColor: "transparent",
                "&:hover": {
                    backgroundColor: "transparent",
                },
                "& > *": {
                    color: "var(--primary-text-color)",
                },
            },
            daysHeader: {
                display: "flex",
                justifyContent: "space-between",
                padding: "0 8px",
            },
            dayLabel: {
                width: "36px",
                textAlign: "center",
                color: "var(--primary-text-color-80)",
            },
        },
        MuiPickersCalendar: {
            week: {
                display: "flex",
                justifyContent: "space-between",
                padding: "0 8px",
            },
        },
        MuiPickersDay: {
            day: {
                width: "36px",
                height: "36px",
                margin: "0",
                color: "var(--primary-text-color)",
                borderRadius: "8px",
            },
            daySelected: {
                backgroundColor: "var(--primary-theme-color)",
                color: "#111111",
                borderRadius: "8px",
                "&:hover": {
                    backgroundColor: "var(--primary-theme-color)",
                },
            },
            /* Today without a journal note: teal outline only (see renderDay for “today + note”). */
            current: {
                backgroundColor: "transparent",
                border: "1px solid var(--primary-theme-color)",
                borderRadius: "8px",
            },
            dayDisabled: {
                color: "var(--primary-text-color-80)",
            },
        },
        MuiPickersBasePicker: {
            pickerView: {
                maxWidth: "100%",
            },
        },
        MuiTypography: {
            alignCenter: {
                textAlign: "left",
                color: "var(--primary-text-color)",
            },
        },
    },
});

export default function MainCalendar({
    selectedDays,
    date,
    setDate,
    getJournalData,
}) {
    // Track the currently displayed month (may differ from selected date when navigating)
    const [displayedMonth, setDisplayedMonth] = React.useState(
        date || new Date(),
    );

    // Check if we're viewing the current month (to disable right arrow)
    const isCurrentMonth = React.useMemo(() => {
        const now = new Date();
        const displayed = displayedMonth || date || new Date();
        return (
            displayed.getMonth() === now.getMonth() &&
            displayed.getFullYear() === now.getFullYear()
        );
    }, [displayedMonth, date]);

    const daysWithNotes = React.useMemo(
        () => new Set(selectedDays || []),
        [selectedDays],
    );

    const renderDay = (day, selectedDate, isInCurrentMonth, dayComponent) => {
        if (!day || !dayComponent) {
            return dayComponent;
        }

        const dayKey = format(day, "yyyy-MM-dd");
        const hasNote = daysWithNotes.has(dayKey);
        const isTodayDate = isToday(day);
        const pickedDate = date || selectedDate;
        const isPicked =
            pickedDate != null && isSameDay(day, pickedDate);

        let extraStyle = {};
        if (isInCurrentMonth && hasNote && isTodayDate) {
            /* Today + note: solid teal fill; number and dot use dark contrast. */
            extraStyle = {
                backgroundColor: "var(--primary-theme-color)",
                color: "#111111",
                border: "none",
            };
        } else if (
            isInCurrentMonth &&
            hasNote &&
            isPicked &&
            !isTodayDate
        ) {
            /* Selected (picked) day + note, not today: teal ring, no fill. */
            extraStyle = {
                backgroundColor: "transparent",
                color: "var(--primary-text-color)",
                border: "1px solid var(--primary-theme-color)",
            };
        }

        const showDot = hasNote && isInCurrentMonth;
        const dotColor =
            hasNote && isTodayDate ? "#111111" : "var(--primary-text-color)";

        const daySize = 36;
        const noteDayPadding =
            showDot ?
                {
                    paddingBottom: 8,
                    paddingTop: 0,
                    boxSizing: "border-box",
                }
            :   {};

        const mergedDayStyle = {
            ...(dayComponent.props.style || {}),
            ...extraStyle,
            ...noteDayPadding,
        };

        const styledDay =
            Object.keys(extraStyle).length > 0 || showDot
                ? React.cloneElement(dayComponent, {
                      style: mergedDayStyle,
                  })
                : dayComponent;

        if (!showDot && Object.keys(extraStyle).length === 0) {
            return styledDay;
        }

        /* Dot sits inside the same rounded square as the day (not below it). */
        return (
            <div
                style={{
                    position: "relative",
                    width: daySize,
                    height: daySize,
                    margin: 0,
                    padding: 0,
                    flexShrink: 0,
                }}
            >
                {styledDay}
                {showDot && (
                    <span
                        aria-hidden
                        style={{
                            position: "absolute",
                            left: "50%",
                            bottom: 5,
                            transform: "translateX(-50%)",
                            width: 4,
                            height: 4,
                            borderRadius: "50%",
                            backgroundColor: dotColor,
                            pointerEvents: "none",
                            zIndex: 1,
                        }}
                    />
                )}
            </div>
        );
    };

    const handleMonthChange = (newMonth) => {
        if (!getJournalData) {
            setDisplayedMonth(newMonth);
            return;
        }

        // Compare against the currently displayed month, not the selected date
        const currentDisplayedMonth = displayedMonth?.getMonth?.();
        const currentDisplayedYear = displayedMonth?.getFullYear?.();
        const newMonthIndex = newMonth?.getMonth?.();
        const newYear = newMonth?.getFullYear?.();

        // Update the displayed month state
        setDisplayedMonth(newMonth);

        // If month and year haven't actually changed, skip calling getJournalData
        if (
            currentDisplayedMonth === newMonthIndex &&
            currentDisplayedYear === newYear
        ) {
            return;
        }

        // Load journal data for the new month
        getJournalData(newMonth);
    };

    return (
        <div
            style={{
                backgroundColor: "var(--primary-popup-background-color)",
                padding: "5px",
                margin: 0,
                width: "100%",
                overflow: "hidden",
                boxSizing: "border-box",
            }}
        >
            <MuiThemeProvider theme={theme}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <Paper
                        style={{
                            backgroundColor:
                                "var(--primary-popup-background-color)",
                            overflow: "hidden",
                            boxShadow: "none",
                            padding: "8px 0",
                            width: "100%",
                        }}
                    >
                        <Calendar
                            date={date}
                            disableFuture={false}
                            disablePast={false}
                            maxDate={new Date()}
                            onChange={(newDate) => setDate(newDate)}
                            renderDay={renderDay}
                            onMonthChange={handleMonthChange}
                            rightArrowButtonProps={{
                                disabled: isCurrentMonth,
                                style: isCurrentMonth
                                    ? { opacity: 0.3, pointerEvents: "none" }
                                    : {},
                            }}
                            leftArrowButtonProps={{ disabled: false }}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                            }}
                        />
                    </Paper>
                </MuiPickersUtilsProvider>
            </MuiThemeProvider>
        </div>
    );
}
